// src/components/events/EventCard.js
// Reusable event card for the Events tab list view.

import React, { useState } from 'react';
import { Calendar, Users, MoreHorizontal, Edit2, Copy, XCircle, Trash2 } from 'lucide-react';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatEventDate(event) {
  const { eventType, schedule } = event;
  const opts = { weekday: 'short', month: 'short', day: 'numeric' };
  const tzAbbr = getTimezoneAbbr(event.timezone);
  const tzSuffix = tzAbbr ? ` ${tzAbbr}` : '';

  if (eventType === 'single' && schedule?.date) {
    const d = new Date(schedule.date + 'T12:00:00');
    const dateStr = d.toLocaleDateString('en-US', opts);
    const start = formatTime12(schedule.startTime);
    const end = formatTime12(schedule.endTime);
    return `${dateStr} • ${start} — ${end}${tzSuffix}`;
  }

  if (eventType === 'series' && schedule?.sessionDates?.length > 0) {
    const first = new Date(schedule.sessionDates[0] + 'T12:00:00');
    const last = new Date(schedule.sessionDates[schedule.sessionDates.length - 1] + 'T12:00:00');
    const firstStr = first.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const lastStr = last.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const time = schedule.recurringTime ? ` • ${formatTime12(schedule.recurringTime)}${tzSuffix}` : '';
    return `${schedule.sessionDates.length} sessions • ${firstStr} — ${lastStr}${time}`;
  }

  if (eventType === 'multi_day' && schedule?.date && schedule?.endDate) {
    const start = new Date(schedule.date + 'T12:00:00');
    const end = new Date(schedule.endDate + 'T12:00:00');
    const startStr = start.toLocaleDateString('en-US', opts);
    const endStr = end.toLocaleDateString('en-US', opts);
    return `${startStr} — ${endStr}${tzSuffix}`;
  }

  return 'Date not set';
}

function formatTime12(time24) {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function getTimezoneAbbr(tz) {
  if (!tz) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date());
    return parts.find(p => p.type === 'timeZoneName')?.value || '';
  } catch { return ''; }
}

function formatPrice(event) {
  const { pricingModel, pricing } = event;
  if (pricingModel === 'free') return 'Free';
  if (pricingModel === 'flat' && pricing?.price) return `$${(pricing.price / 100).toFixed(2)}`;
  if (pricingModel === 'early_bird' && pricing?.price) return `$${(pricing.price / 100).toFixed(2)}`;
  if (pricingModel === 'deposit' && pricing?.totalPrice) return `$${(pricing.totalPrice / 100).toFixed(2)}`;
  return 'Price not set';
}

function getStatusBadge(event) {
  const { status, currentAttendees, maxAttendees } = event;
  if (status === 'cancelled') return { label: 'Cancelled', bg: '#FEF2F2', color: '#DC2626' };
  if (status === 'completed') return { label: 'Completed', bg: '#EFF6FF', color: '#2563EB' };
  if (status === 'draft') return { label: 'Draft', bg: '#F3F4F6', color: '#6B7280' };
  if (currentAttendees >= maxAttendees) return { label: 'Sold Out', bg: '#FFFBEB', color: '#D97706' };
  return { label: 'Open', bg: '#F0FDF4', color: '#16A34A' };
}

function getFormatLabel(format) {
  if (format === 'virtual') return 'Virtual';
  if (format === 'in_person') return 'In-Person';
  if (format === 'hybrid') return 'Hybrid';
  return '';
}

function getEventSortDate(event) {
  const { eventType, schedule } = event;
  if (eventType === 'single' && schedule?.date) return schedule.date;
  if (eventType === 'series' && schedule?.sessionDates?.[0]) return schedule.sessionDates[0];
  if (eventType === 'multi_day' && schedule?.date) return schedule.date;
  return '9999-12-31'; // no date = sort to end
}

// ─── Gradient placeholder for events without cover image ────────────────────

const GRADIENTS = [
  'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
  'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
  'linear-gradient(135deg, #8b5cf6 0%, #c4b5fd 100%)',
  'linear-gradient(135deg, #7c3aed 0%, #22d3ee 100%)',
];

function getGradient(eventId) {
  let hash = 0;
  for (let i = 0; i < (eventId || '').length; i++) {
    hash = ((hash << 5) - hash) + eventId.charCodeAt(i);
    hash |= 0;
  }
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length];
}

// ─── Component ──────────────────────────────────────────────────────────────

const EventCard = ({ event, onEdit, onDuplicate, onCancel, onDelete, onView }) => {
  const [showMenu, setShowMenu] = useState(false);
  const statusBadge = getStatusBadge(event);
  const progressPct = event.maxAttendees > 0 ? Math.min((event.currentAttendees / event.maxAttendees) * 100, 100) : 0;

  return (
    <div style={{
      background: '#FFFFFF',
      border: '1px solid #E5E7EB',
      borderRadius: 12,
      overflow: 'visible',
      transition: 'box-shadow 0.15s',
      position: 'relative',
    }}
      onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'}
      onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
    >
      {/* Cover image / gradient */}
      <div style={{
        height: 120,
        background: event.coverImage ? `url(${event.coverImage}) center/cover` : getGradient(event.id),
        position: 'relative',
        borderRadius: '12px 12px 0 0',
        overflow: 'hidden',
      }}>
        {/* Format badge overlay */}
        <span style={{
          position: 'absolute', top: 10, left: 10,
          padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
          fontFamily: 'Montserrat, sans-serif',
          background: 'rgba(255,255,255,0.9)', color: '#374151',
          backdropFilter: 'blur(4px)',
        }}>
          {getFormatLabel(event.format)}
        </span>
      </div>

      {/* Card body */}
      <div style={{ padding: 16 }}>
        {/* Title */}
        <h3 style={{
          fontSize: 16, fontWeight: 700, color: '#111827',
          fontFamily: 'Montserrat, sans-serif', margin: '0 0 6px',
          lineHeight: 1.3,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {event.title || 'Untitled Event'}
        </h3>

        {/* Date/time */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <Calendar size={13} color="#9CA3AF" />
          <span style={{ fontSize: 13, color: '#6B7280', fontFamily: 'Montserrat, sans-serif' }}>
            {formatEventDate(event)}
          </span>
        </div>

        {/* Registration progress */}
        <div style={{ marginTop: 12, marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: 12, color: '#6B7280', fontFamily: 'Montserrat, sans-serif' }}>
              {event.currentAttendees || 0} of {event.maxAttendees} registered
            </span>
          </div>
          <div style={{ height: 6, background: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: `${progressPct}%`,
              background: progressPct >= 100 ? '#D97706' : '#7c3aed',
              borderRadius: 3,
              transition: 'width 0.3s',
            }} />
          </div>
        </div>

        {/* Price + status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: '#374151', fontFamily: 'Montserrat, sans-serif' }}>
            {formatPrice(event)}
            {event.pricingModel !== 'free' && <span style={{ fontSize: 12, fontWeight: 400, color: '#9CA3AF' }}> per person</span>}
          </span>
          <span style={{
            padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
            fontFamily: 'Montserrat, sans-serif',
            background: statusBadge.bg, color: statusBadge.color,
          }}>
            {statusBadge.label}
          </span>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderTop: '1px solid #F3F4F6', paddingTop: 12 }}>
          {event.status !== 'draft' && (
            <span
              role="button"
              tabIndex={0}
              onClick={() => onView?.(event)}
              onKeyDown={(e) => e.key === 'Enter' && onView?.(event)}
              style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 500,
                fontFamily: 'Montserrat, sans-serif',
                color: '#FFFFFF', background: '#7c3aed',
                cursor: 'pointer', userSelect: 'none',
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            >
              <Users size={13} />
              Manage
            </span>
          )}

          <span
            role="button"
            tabIndex={0}
            onClick={() => onEdit?.(event)}
            onKeyDown={(e) => e.key === 'Enter' && onEdit?.(event)}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 500,
              fontFamily: 'Montserrat, sans-serif',
              color: '#7c3aed', background: '#F5F3FF',
              cursor: 'pointer', userSelect: 'none',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#EDE9FE'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#F5F3FF'}
          >
            <Edit2 size={13} />
            Edit
          </span>

          <span
            role="button"
            tabIndex={0}
            onClick={() => onDuplicate?.(event)}
            onKeyDown={(e) => e.key === 'Enter' && onDuplicate?.(event)}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 500,
              fontFamily: 'Montserrat, sans-serif',
              color: '#374151', background: '#F3F4F6',
              cursor: 'pointer', userSelect: 'none',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#E5E7EB'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#F3F4F6'}
          >
            <Copy size={13} />
            Duplicate
          </span>

          {/* Overflow menu — only show if there are menu items */}
          {(event.status === 'published' || event.status === 'draft' || event.status === 'cancelled') && (
          <div style={{ position: 'relative', marginLeft: 'auto' }}>
            <span
              role="button"
              tabIndex={0}
              onClick={() => setShowMenu(!showMenu)}
              onKeyDown={(e) => e.key === 'Enter' && setShowMenu(!showMenu)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 32, height: 32, borderRadius: 6,
                color: '#9CA3AF', cursor: 'pointer', userSelect: 'none',
                background: showMenu ? '#F3F4F6' : 'transparent',
                transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#F3F4F6'}
              onMouseLeave={(e) => { if (!showMenu) e.currentTarget.style.background = 'transparent'; }}
            >
              <MoreHorizontal size={16} />
            </span>

            {showMenu && (
              <>
                {/* Backdrop to close menu */}
                <div
                  style={{ position: 'fixed', inset: 0, zIndex: 10 }}
                  onClick={() => setShowMenu(false)}
                />
                <div style={{
                  position: 'absolute', right: 0, top: 36, zIndex: 20,
                  background: '#FFFFFF', border: '1px solid #E5E7EB',
                  borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  minWidth: 160, overflow: 'hidden',
                }}>
                  {event.status === 'published' && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => { setShowMenu(false); onCancel?.(event); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setShowMenu(false); onCancel?.(event); } }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '10px 14px', fontSize: 13, fontWeight: 500,
                        fontFamily: 'Montserrat, sans-serif',
                        color: '#DC2626', cursor: 'pointer', userSelect: 'none',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#FEF2F2'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <XCircle size={14} />
                      Cancel event
                    </span>
                  )}
                  {(event.status === 'draft' || event.status === 'cancelled') && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => { setShowMenu(false); onDelete?.(event); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') { setShowMenu(false); onDelete?.(event); } }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '10px 14px', fontSize: 13, fontWeight: 500,
                        fontFamily: 'Montserrat, sans-serif',
                        color: '#DC2626', cursor: 'pointer', userSelect: 'none',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#FEF2F2'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <Trash2 size={14} />
                      Delete
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Export helper for sorting
export { getEventSortDate };
export default EventCard;
